import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Helper from './helper';

const DEVICE_ID_KEY = 'device_id';

export const getOrCreateDeviceId = () => {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = uuidv4 ? uuidv4() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch (e) {
    return 'unknown-device';
  }
};

export const extractJwtToken = (str) => {
  if (!str || typeof str !== 'string') return null;
  const match = str.match(/eyJ[a-zA-Z0-9_-]+?\.[a-zA-Z0-9_-]+?\.[a-zA-Z0-9_-]+/);
  return match ? match[0] : null;
};

export const extractToken = () => {
  try {
    // 1. Direct JWT from URL
    const urlJwt = extractJwtToken(window.location.href);
    if (urlJwt) {
      try { localStorage.setItem('token', urlJwt); } catch (e) {}
      return urlJwt;
    }

    // 2. Query or hash param
    const searchParams = new URLSearchParams(window.location.search || '');
    const hashIdx = window.location.hash.indexOf('?');
    const hashParams = hashIdx !== -1
      ? new URLSearchParams(window.location.hash.slice(hashIdx))
      : new URLSearchParams();
    const queryToken = searchParams.get('token') || hashParams.get('token') ||
                       searchParams.get('access_token') || hashParams.get('access_token') ||
                       (Helper.getParamUrlByKey ? Helper.getParamUrlByKey('token') : null);
    if (queryToken && queryToken !== 'null' && queryToken !== 'undefined') {
      const qJwt = extractJwtToken(queryToken);
      const valid = qJwt || queryToken;
      try { localStorage.setItem('token', valid); } catch (e) {}
      return valid;
    }

    // 3. Dot-separated token in pathname: e.g. /tai-nan-ho-gia-dinh.x3musb9l
    const pathname = window.location.pathname || '';
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments.pop() || '';

    if (lastSegment.includes('.')) {
      const dotParts = lastSegment.split('.');
      const candidate = dotParts.pop();
      const ignoredExts = ['html', 'htm', 'js', 'css', 'json', 'png', 'jpg', 'jpeg', 'svg', 'ico'];
      if (candidate && !ignoredExts.includes(candidate.toLowerCase())) {
        try { localStorage.setItem('token', candidate); } catch (e) {}
        return candidate;
      }
    }

    // 4. Slash-separated token in pathname: e.g. /tai-nan-ho-gia-dinh/x3musb9l
    const knownRoutes = [
      'tai-nan-ho-gia-dinh',
      'tai-nan-gia-dinh',
      'bao-hiem-tai-nan-ho-gia-dinh',
      'an-gia-247',
      'bao-hiem-an-gia-247',
      'san-pham',
      'packeds',
      'intro'
    ];
    if (lastSegment && !knownRoutes.includes(lastSegment)) {
      try { localStorage.setItem('token', lastSegment); } catch (e) {}
      return lastSegment;
    }

    // 5. Stored token in localStorage or sessionStorage (fallback only)
    try {
      const stored = localStorage.getItem('token') || localStorage.getItem('access_token') || sessionStorage.getItem('token');
      if (stored && stored !== 'null' && stored !== 'undefined') return stored;
    } catch (e) {}

    return '';
  } catch (e) {
    return '';
  }
};

const OPEN_API_BASE_URL =
  process.env.REACT_APP_OPEN_API || 'https://open-api-dev.globalcare.vn';

export const getUserIdFromToken = (token) => {
  try {
    if (!token || typeof token !== "string") return null;
    const jwt = extractJwtToken(token) || token;
    if (!jwt.includes(".")) return null;
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    let base64Url = parts[1];
    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
      base64 += "=";
    }
    let jsonPayload;
    try {
      jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
    } catch (err) {
      jsonPayload = atob(base64);
    }
    const parsed = JSON.parse(jsonPayload);
    return (
      parsed?.userId ||
      parsed?.user_id ||
      parsed?.id ||
      parsed?.sub ||
      parsed?.staffId ||
      parsed?.staff_id ||
      parsed?.partnerStaffId ||
      parsed?.partner_staff_id ||
      parsed?.partner_id ||
      parsed?.partnerId ||
      parsed?.data?.userId ||
      parsed?.data?.user_id ||
      parsed?.data?.id ||
      parsed?.data?.staffId ||
      parsed?.data?.staff_id ||
      parsed?.user?.id ||
      parsed?.user?.userId ||
      parsed?.user?.user_id ||
      null
    );
  } catch (e) {
    return null;
  }
};

/**
 * Robust helper to resolve user_id from URL params, decoded token, or storage
 */
export const getTrackingUserId = (fallbackId = null) => {
  try {
    const searchParams = new URLSearchParams(window.location.search || "");
    const hashIndex = window.location.hash.indexOf("?");
    const hashParams =
      hashIndex !== -1
        ? new URLSearchParams(window.location.hash.slice(hashIndex))
        : new URLSearchParams();

    const paramKeys = [
      "user_id",
      "userId",
      "staff_id",
      "staffId",
      "partner_staff_id",
      "partnerStaffId",
      "customer_id",
      "customerId",
      "buyer_id",
      "buyerId",
      "uid",
    ];

    for (const key of paramKeys) {
      const val = searchParams.get(key) || hashParams.get(key);
      if (val && val !== "null" && val !== "undefined") {
        return isNaN(val) ? val : Number(val);
      }
    }

    let token =
      searchParams.get("token") ||
      hashParams.get("token") ||
      searchParams.get("access_token") ||
      hashParams.get("access_token");

    if (!token && typeof extractToken === "function") {
      token = extractToken();
    }
    if (!token) {
      try {
        token =
          localStorage.getItem("token") ||
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("token");
      } catch (e) {}
    }
    if (token) {
      const tokenUserId = getUserIdFromToken(token);
      if (tokenUserId) {
        return isNaN(tokenUserId) ? tokenUserId : Number(tokenUserId);
      }
    }

    try {
      for (const key of ["user_id", "userId", "staff_id", "staffId"]) {
        const val = localStorage.getItem(key);
        if (val && val !== "null" && val !== "undefined") {
          return isNaN(val) ? val : Number(val);
        }
      }
      const userInfo = JSON.parse(
        localStorage.getItem("userInfo") ||
          localStorage.getItem("user") ||
          "{}"
      );
      const uid =
        userInfo?.id ||
        userInfo?.userId ||
        userInfo?.user_id ||
        userInfo?.staffId ||
        userInfo?.staff_id;
      if (uid) return isNaN(uid) ? uid : Number(uid);

      const collab = JSON.parse(
        localStorage.getItem("collaboratorInfo") || "{}"
      );
      const cid = collab?.id || collab?.userId || collab?.staffId;
      if (cid) return isNaN(cid) ? cid : Number(cid);
    } catch (e) {}

    if (fallbackId && fallbackId !== "null" && fallbackId !== "undefined") {
      return isNaN(fallbackId) ? fallbackId : Number(fallbackId);
    }

    return null;
  } catch (e) {
    return fallbackId || null;
  }
};

/**
 * Send tracking event to Open API server (non-blocking)
 * @param {string} eventName
 * @param {object} payload
 */
export const trackProductEvent = async (eventName, payload = {}) => {
  try {
    const token = extractToken();
    const deviceId = getOrCreateDeviceId();

    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const resolvedPayload = { ...payload };
    if (
      resolvedPayload.user_id === null ||
      resolvedPayload.user_id === undefined
    ) {
      const detectedUserId = getTrackingUserId();
      if (detectedUserId !== null && detectedUserId !== undefined) {
        resolvedPayload.user_id = detectedUserId;
      }
    }

    const body = {
      eventName,
      occurredAt: new Date().toISOString(),
      deviceId,
      platform: 'product',
      payload: resolvedPayload,
    };

    // Non-blocking fire and forget
    axios({
      method: 'POST',
      url: `${OPEN_API_BASE_URL}/v1/products/event-tracking`,
      data: body,
      headers,
      timeout: 5000,
    }).catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[Tracking Error] ${eventName}:`, err?.response?.data || err.message);
      }
    });
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[Tracking Exception] ${eventName}:`, e?.message);
    }
  }
};

// Debounce helper for field inputs
const debounceTimers = {};
export const debounceTrackFieldInput = (eventName, fieldName, fieldValue, delay = 500) => {
  const key = `${eventName}_${fieldName}`;
  if (debounceTimers[key]) {
    clearTimeout(debounceTimers[key]);
  }
  debounceTimers[key] = setTimeout(() => {
    trackProductEvent(eventName, {
      field_name: fieldName,
    });
    delete debounceTimers[key];
  }, delay);
};

export default {
  trackProductEvent,
  debounceTrackFieldInput,
  getOrCreateDeviceId,
  extractToken,
  getUserIdFromToken,
  getTrackingUserId,
};
